export interface DomGrabOptions {
  hue?: number;
  showCrosshair?: boolean;
  maxHtmlLength?: number;
  includeStyles?: boolean;
  keyHoldDuration?: number;
  onCopy?: (context: string) => void;
}

export interface DomGrabAPI {
  activate: () => void;
  deactivate: () => void;
  destroy: () => void;
}

const DEFAULT_OPTIONS: Required<DomGrabOptions> = {
  hue: 320,
  showCrosshair: true,
  maxHtmlLength: 2000,
  includeStyles: true,
  keyHoldDuration: 200,
  onCopy: () => {}
};

// Console error capture - shared across instances
interface CapturedError {
  message: string;
  timestamp: number;
}

const recentErrors: CapturedError[] = [];
const MAX_ERRORS = 10;
const ERROR_TTL = 30000; // 30 seconds
let patchCount = 0;
let originalConsoleError: typeof console.error | null = null;

function safeStringify(obj: unknown): string {
  try {
    return JSON.stringify(obj);
  } catch {
    return String(obj);
  }
}

function onWindowError(e: ErrorEvent): void {
  recentErrors.push({
    message: `${e.message} at ${e.filename}:${e.lineno}:${e.colno}`,
    timestamp: Date.now()
  });
  if (recentErrors.length > MAX_ERRORS) recentErrors.shift();
}

function patchErrorCapture(): void {
  patchCount++;
  if (patchCount > 1) return; // Already patched

  originalConsoleError = console.error;
  console.error = (...args: unknown[]) => {
    recentErrors.push({
      message: args.map(a => typeof a === 'object' ? safeStringify(a) : String(a)).join(' '),
      timestamp: Date.now()
    });
    if (recentErrors.length > MAX_ERRORS) recentErrors.shift();
    originalConsoleError!.apply(console, args);
  };

  window.addEventListener('error', onWindowError);
}

function unpatchErrorCapture(): void {
  patchCount--;
  if (patchCount > 0) return;

  if (originalConsoleError) {
    console.error = originalConsoleError;
    originalConsoleError = null;
  }
  window.removeEventListener('error', onWindowError);
}

function getRecentErrors(): string[] {
  const now = Date.now();
  return recentErrors
    .filter(e => now - e.timestamp < ERROR_TTL)
    .map(e => e.message);
}

const MAX_FIBER_DEPTH = 20;

function getFrameworkComponent(el: Element): string | null {
  const elAny = el as unknown as Record<string, unknown>;

  // React - check for fiber
  const fiberKey = Object.keys(el).find(k => k.startsWith('__reactFiber$') || k.startsWith('__reactInternalInstance$'));
  if (fiberKey) {
    const fiber = elAny[fiberKey] as Record<string, unknown> | undefined;
    if (fiber) {
      let current = fiber;
      let depth = 0;
      while (current && depth < MAX_FIBER_DEPTH) {
        const type = current.type as Record<string, unknown> | string | undefined;
        if (type && typeof type === 'function') {
          const name = (type as { displayName?: string; name?: string }).displayName || (type as { name?: string }).name;
          if (name && !name.startsWith('_')) return `React: <${name}>`;
        }
        if (type && typeof type === 'object' && (type as Record<string, unknown>).$$typeof) {
          const innerType = (type as { type?: { displayName?: string; name?: string } }).type;
          if (innerType) {
            const name = innerType.displayName || innerType.name;
            if (name) return `React: <${name}>`;
          }
        }
        current = current.return as Record<string, unknown>;
        depth++;
      }
    }
  }

  // Vue 3
  const vueInstance = elAny.__vueParentComponent as Record<string, unknown> | undefined;
  if (vueInstance) {
    const type = vueInstance.type as { name?: string; __name?: string } | undefined;
    const name = type?.name || type?.__name;
    if (name) return `Vue: <${name}>`;
  }

  // Vue 2
  const vue2 = elAny.__vue__ as Record<string, unknown> | undefined;
  if (vue2) {
    const opts = vue2.$options as { name?: string; _componentTag?: string } | undefined;
    const name = opts?.name || opts?._componentTag;
    if (name) return `Vue: <${name}>`;
  }

  // Angular
  const ngKey = Object.keys(el).find(k => k.startsWith('__ng'));
  if (ngKey) {
    const ng = elAny[ngKey];
    if (ng && typeof ng === 'object') {
      const ctor = (ng as Record<string, unknown>).constructor as { name?: string } | undefined;
      if (ctor?.name && ctor.name !== 'Object') return `Angular: ${ctor.name}`;
    }
  }

  // Svelte
  const svelteKey = Object.keys(el).find(k => k.startsWith('__svelte'));
  if (svelteKey) return 'Svelte component';

  return null;
}

function getAccessibility(el: Element): string {
  const lines: string[] = [];

  const role = el.getAttribute('role');
  if (role) lines.push(`role: ${role}`);

  const ariaAttrs = Array.from(el.attributes)
    .filter(a => a.name.startsWith('aria-'))
    .map(a => `${a.name}: ${a.value}`);
  lines.push(...ariaAttrs);

  const tabIndex = el.getAttribute('tabindex');
  if (tabIndex) lines.push(`tabindex: ${tabIndex}`);

  if (el.tagName === 'IMG') {
    const alt = el.getAttribute('alt');
    lines.push(alt ? `alt: "${alt}"` : 'alt: MISSING');
  }

  if (el.tagName === 'A') {
    const href = el.getAttribute('href');
    if (href) lines.push(`href: ${href}`);
  }

  if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT') {
    const name = el.getAttribute('name');
    const type = el.getAttribute('type');
    if (name) lines.push(`name: ${name}`);
    if (type) lines.push(`type: ${type}`);

    // Check for associated label
    const id = el.id;
    if (id) {
      const labels = document.getElementsByTagName('label');
      for (const lbl of labels) {
        if (lbl.htmlFor === id) {
          lines.push(`label: "${lbl.textContent?.trim()}"`);
          break;
        }
      }
    }
  }

  return lines.join('\n');
}

function hslToRgb(h: number, s: number, l: number): string {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0, g = 0, b = 0;
  if (h < 60) { r = c; g = x; }
  else if (h < 120) { r = x; g = c; }
  else if (h < 180) { g = c; b = x; }
  else if (h < 240) { g = x; b = c; }
  else if (h < 300) { r = x; b = c; }
  else { r = c; b = x; }
  return `${Math.round((r + m) * 255)}, ${Math.round((g + m) * 255)}, ${Math.round((b + m) * 255)}`;
}

export function init(userOptions: DomGrabOptions = {}): DomGrabAPI {
  const options = { ...DEFAULT_OPTIONS, ...userOptions };
  const primaryRgb = hslToRgb(options.hue, 0.7, 0.5);
  const lightBg = hslToRgb(options.hue, 0.8, 0.97);
  const borderRgb = hslToRgb(options.hue, 0.7, 0.8);

  let active = false;
  let hoveredEl: Element | null = null;
  let mouseX = 0, mouseY = 0;
  let targetX = 0, targetY = 0;
  let animationId: number | null = null;
  let holdTimerId: number | null = null;
  let holdingKeys = false;

  // Shadow DOM container
  const host = document.createElement('dom-grab-root');
  host.style.cssText = 'position: fixed; inset: 0; pointer-events: none; z-index: 2147483647;';
  const shadow = host.attachShadow({ mode: 'closed' });
  document.body.appendChild(host);

  // Canvas for crosshair
  const canvas = document.createElement('canvas');
  canvas.style.cssText = 'position: fixed; inset: 0; pointer-events: none;';
  shadow.appendChild(canvas);
  const maybeCtx = canvas.getContext('2d');
  if (!maybeCtx) {
    console.warn('dom-grab: Canvas 2D context unavailable');
    host.remove();
    return { activate: () => {}, deactivate: () => {}, destroy: () => {} };
  }
  const ctx = maybeCtx;

  // Element highlight overlay
  const overlay = document.createElement('div');
  overlay.style.cssText = `
    position: fixed; pointer-events: none; display: none;
    border: 2px dashed rgb(${primaryRgb});
    background: rgba(${primaryRgb}, 0.1);
    transition: all 0.05s ease-out;
  `;
  shadow.appendChild(overlay);

  // Label tooltip
  const label = document.createElement('div');
  label.style.cssText = `
    position: fixed; pointer-events: none; display: none;
    background: rgb(${lightBg}); color: rgb(${primaryRgb});
    border: 1px solid rgb(${borderRgb});
    padding: 2px 8px; border-radius: 4px;
    font: 11px/1.4 ui-monospace, SFMono-Regular, Menlo, Monaco, monospace;
    white-space: nowrap; max-width: 400px; overflow: hidden; text-overflow: ellipsis;
  `;
  shadow.appendChild(label);

  // Copied feedback
  const feedback = document.createElement('div');
  feedback.style.cssText = `
    position: fixed; pointer-events: none; display: none;
    background: rgb(${primaryRgb}); color: white;
    padding: 8px 16px; border-radius: 8px;
    font: 13px/1.4 -apple-system, BlinkMacSystemFont, sans-serif;
    transform: translate(-50%, -50%);
    transition: opacity 0.3s ease;
  `;
  feedback.textContent = 'Copied!';
  shadow.appendChild(feedback);

  function resizeCanvas() {
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
  }

  function getSelector(el: Element): string {
    const tag = el.tagName.toLowerCase();
    const id = el.id ? `#${el.id}` : '';
    const classes = el.classList.length > 0
      ? '.' + Array.from(el.classList).join('.')
      : '';
    return `${tag}${id}${classes}`;
  }

  function getDomPath(el: Element): string {
    const path: string[] = [];
    let current: Element | null = el;
    while (current && current !== document.body && current !== document.documentElement) {
      path.unshift(getSelector(current));
      current = current.parentElement;
    }
    return path.join(' > ');
  }

  function getAttributes(el: Element): string {
    return Array.from(el.attributes)
      .map(a => `${a.name}="${a.value}"`)
      .join('\n');
  }

  function getKeyStyles(el: Element): string {
    const computed = window.getComputedStyle(el);
    const keys = ['display', 'position', 'width', 'height', 'padding', 'margin',
                  'flex', 'grid', 'background', 'color', 'font-size', 'z-index'];
    const skipValues = ['', 'none', '0px', 'auto', 'normal', 'static', 'visible', 'baseline', 'stretch'];
    return keys
      .map(k => ({ key: k, value: computed.getPropertyValue(k) }))
      .filter(({ value }) => value && !skipValues.includes(value))
      .map(({ key, value }) => `${key}: ${value}`)
      .join('\n');
  }

  function formatContext(el: Element): string {
    const selector = getSelector(el);
    const path = getDomPath(el);
    const attrs = getAttributes(el);
    const framework = getFrameworkComponent(el);
    const a11y = getAccessibility(el);
    const errors = getRecentErrors();

    let html = el.outerHTML;
    if (html.length > options.maxHtmlLength) {
      html = html.slice(0, options.maxHtmlLength) + '\n<!-- truncated -->';
    }

    let output = `<selected_element>
## Page
${window.location.href}

## Element
${selector}`;

    if (framework) {
      output += `

## Framework Component
${framework}`;
    }

    output += `

## DOM Path
${path}

## Attributes
${attrs}`;

    if (a11y) {
      output += `

## Accessibility
${a11y}`;
    }

    output += `

## HTML
\`\`\`html
${html}
\`\`\``;

    if (options.includeStyles) {
      output += `

## Key Styles
${getKeyStyles(el)}`;
    }

    if (errors.length > 0) {
      output += `

## Recent Console Errors
${errors.map(e => `- ${e}`).join('\n')}`;
    }

    output += '\n</selected_element>';
    return output;
  }

  function draw() {
    if (!active) return;

    // Lerp mouse position for smooth crosshair
    mouseX += (targetX - mouseX) * 0.3;
    mouseY += (targetY - mouseY) * 0.3;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (options.showCrosshair) {
      ctx.strokeStyle = `rgba(${primaryRgb}, 0.6)`;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(mouseX, 0);
      ctx.lineTo(mouseX, canvas.height);
      ctx.moveTo(0, mouseY);
      ctx.lineTo(canvas.width, mouseY);
      ctx.stroke();
    }

    animationId = requestAnimationFrame(draw);
  }

  function updateOverlay() {
    if (!hoveredEl) {
      overlay.style.display = 'none';
      label.style.display = 'none';
      return;
    }

    const rect = hoveredEl.getBoundingClientRect();
    overlay.style.display = 'block';
    overlay.style.left = `${rect.left}px`;
    overlay.style.top = `${rect.top}px`;
    overlay.style.width = `${rect.width}px`;
    overlay.style.height = `${rect.height}px`;

    label.style.display = 'block';
    label.textContent = getSelector(hoveredEl);

    // Position label near cursor, keeping in viewport
    let labelX = targetX + 12;
    let labelY = targetY + 12;
    const labelRect = label.getBoundingClientRect();

    if (labelX + labelRect.width > window.innerWidth - 10) {
      labelX = targetX - labelRect.width - 12;
    }
    if (labelY + labelRect.height > window.innerHeight - 10) {
      labelY = targetY - labelRect.height - 12;
    }

    label.style.left = `${labelX}px`;
    label.style.top = `${labelY}px`;
  }

  function showCopiedFeedback() {
    feedback.style.display = 'block';
    feedback.style.left = `${targetX}px`;
    feedback.style.top = `${targetY}px`;
    feedback.style.opacity = '1';

    setTimeout(() => {
      feedback.style.opacity = '0';
      setTimeout(() => {
        feedback.style.display = 'none';
      }, 300);
    }, 800);
  }

  function onMouseMove(e: MouseEvent) {
    if (!active) return;
    targetX = e.clientX;
    targetY = e.clientY;

    // Find element under cursor (excluding our overlay elements)
    host.style.display = 'none';
    const el = document.elementFromPoint(e.clientX, e.clientY);
    host.style.display = '';

    if (el && el !== hoveredEl) {
      hoveredEl = el;
      updateOverlay();
    }
  }

  function onClick(e: MouseEvent) {
    if (!active || !hoveredEl) return;

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    const context = formatContext(hoveredEl);
    navigator.clipboard.writeText(context)
      .then(() => {
        feedback.textContent = 'Copied!';
        showCopiedFeedback();
        options.onCopy(context);
      })
      .catch(() => {
        feedback.textContent = 'Copy failed';
        showCopiedFeedback();
      });

    deactivate();
  }

  function clearHoldTimer() {
    if (holdTimerId !== null) {
      clearTimeout(holdTimerId);
      holdTimerId = null;
    }
    holdingKeys = false;
  }

  function onKeyDown(e: KeyboardEvent) {
    // Escape to cancel
    if (active && e.code === 'Escape') {
      deactivate();
      return;
    }

    // Cmd+C / Ctrl+C - start hold timer
    if ((e.metaKey || e.ctrlKey) && e.code === 'KeyC' && !active && !holdingKeys) {
      holdingKeys = true;
      holdTimerId = window.setTimeout(() => {
        // Held long enough - activate and prevent native copy
        holdTimerId = null;
        activate();
      }, options.keyHoldDuration);
    }
  }

  function onKeyUp(e: KeyboardEvent) {
    // If releasing C or modifier before timer fires, allow native copy
    if (e.code === 'KeyC' || e.code === 'MetaLeft' || e.code === 'MetaRight' ||
        e.code === 'ControlLeft' || e.code === 'ControlRight') {
      clearHoldTimer();
      if (active) {
        deactivate();
      }
    }
  }

  function onCopyEvent(e: ClipboardEvent) {
    // Prevent native copy when hold timer is pending or already activated
    if (active || holdTimerId !== null) {
      e.preventDefault();
    }
  }

  function activate() {
    if (active) return;
    active = true;
    resizeCanvas();
    host.style.display = '';
    document.body.style.cursor = 'crosshair';
    draw();
  }

  function deactivate() {
    if (!active) return;
    active = false;
    hoveredEl = null;
    if (animationId) {
      cancelAnimationFrame(animationId);
      animationId = null;
    }
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    overlay.style.display = 'none';
    label.style.display = 'none';
    document.body.style.cursor = '';
  }

  function destroy() {
    deactivate();
    clearHoldTimer();
    document.removeEventListener('keydown', onKeyDown, true);
    document.removeEventListener('keyup', onKeyUp, true);
    document.removeEventListener('mousemove', onMouseMove, true);
    document.removeEventListener('click', onClick, true);
    document.removeEventListener('copy', onCopyEvent, true);
    window.removeEventListener('resize', resizeCanvas);
    unpatchErrorCapture();
    host.remove();
  }

  // Setup error capturing
  patchErrorCapture();

  // Event listeners
  document.addEventListener('keydown', onKeyDown, true);
  document.addEventListener('keyup', onKeyUp, true);
  document.addEventListener('mousemove', onMouseMove, true);
  document.addEventListener('click', onClick, true);
  document.addEventListener('copy', onCopyEvent, true);
  window.addEventListener('resize', resizeCanvas);

  resizeCanvas();
  host.style.display = 'none'; // Hidden until activated

  return { activate, deactivate, destroy };
}

// Auto-init when loaded via script tag (IIFE build only, not ES modules)
// For ES modules, document.currentScript is null - users should call init() explicitly
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  const script = document.currentScript as HTMLScriptElement | null;
  // Only auto-init for script tags (IIFE), not ES modules
  if (script && script.dataset.enabled !== 'false') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => init());
    } else {
      init();
    }
  }
}
